import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';

const STATUS_MAP = {
  active:    { label: 'Activo',      bg: 'rgba(22,163,74,0.12)',  color: '#16A34A', border: '#16A34A' },
  scheduled: { label: 'Programado',  bg: 'rgba(37,99,235,0.10)',  color: '#2563EB', border: '#2563EB' },
  closed:    { label: 'Cerrado',     bg: '#F0F2F8',               color: '#6B7A99', border: '#CBD0DB' },
  pending:   { label: 'Pendiente',   bg: 'rgba(217,119,6,0.10)',  color: '#D97706', border: '#D97706' },
  live:      { label: 'En vivo',     bg: 'rgba(22,163,74,0.12)',  color: '#16A34A', border: '#16A34A' },
  finished:  { label: 'Finalizado',  bg: '#F0F2F8',               color: '#6B7A99', border: '#CBD0DB' },
  ended:     { label: 'Finalizado',  bg: '#F0F2F8',               color: '#6B7A99', border: '#CBD0DB' },
  cancelled: { label: 'Cancelado',   bg: 'rgba(220,38,38,0.08)',  color: '#DC2626', border: '#DC2626' },
  canceled:  { label: 'Cancelado',   bg: 'rgba(220,38,38,0.08)',  color: '#DC2626', border: '#DC2626' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[(status || '').toLowerCase()] || { label: status || '—', bg: '#E2E5EC', color: '#6B7A99', border: '#CBD0DB' };
  return (
    <span style={{
      fontSize: '0.6875rem',
      fontWeight: 500,
      padding: '2px 7px',
      borderRadius: 9999,
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
    }}>
      {cfg.label}
    </span>
  );
}

StatusBadge.propTypes = { status: PropTypes.string };

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/**
 * Calendario semanal Lun–Dom con remates por día. Cada remate es un enlace a su sala.
 */
function WeeklyCalendar({ auctions }) {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });

  const days = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const auctionsByDay = useMemo(() => {
    const map = {};
    days.forEach(d => { map[format(d, 'yyyy-MM-dd')] = []; });
    auctions.forEach(a => {
      const key = format(parseISO(a.start_date), 'yyyy-MM-dd');
      if (map[key]) map[key].push(a);
    });
    return map;
  }, [auctions, days]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))',
        gap: 8,
        minWidth: 910,
      }}>
        {days.map((day, idx) => {
          const key = format(day, 'yyyy-MM-dd');
          const isToday = isSameDay(day, today);
          const dayAuctions = auctionsByDay[key] || [];

          return (
            <div key={key} style={{
              borderRadius: 10,
              border: isToday ? '2px solid #1F50FF' : '1px solid #E2E5EC',
              background: isToday ? '#EEF2FF' : '#fff',
              minHeight: 120,
              overflow: 'hidden',
              boxShadow: isToday
                ? '0 0 0 4px rgba(31,80,255,0.10)'
                : '0 1px 3px rgba(2,19,68,0.05)',
            }}>
              {/* Day header */}
              <div style={{
                padding: '8px 10px 6px',
                background: isToday ? '#1F50FF' : '#F8F9FC',
                borderBottom: `1px solid ${isToday ? 'transparent' : '#E2E5EC'}`,
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
              }}>
                <span style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: isToday ? 'rgba(255,255,255,0.8)' : '#6B7A99',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                  {DAY_NAMES[idx]}
                </span>
                <span style={{ fontSize: '1.1rem', fontWeight: 600, color: isToday ? '#fff' : '#021344' }}>
                  {format(day, 'd')}
                </span>
                {dayAuctions.length > 0 && (
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    background: isToday ? 'rgba(255,255,255,0.25)' : 'rgba(31,80,255,0.12)',
                    color: isToday ? '#fff' : '#1F50FF',
                    borderRadius: 9999,
                    padding: '1px 7px',
                  }}>
                    {dayAuctions.length}
                  </span>
                )}
              </div>

              {/* Auction cards */}
              <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dayAuctions.length === 0 ? (
                  <span style={{ fontSize: '0.75rem', color: '#CBD0DB', fontStyle: 'italic', paddingTop: 6 }}>
                    Sin remates
                  </span>
                ) : (
                  dayAuctions.map(a => {
                    const href = a.tenant_domain
                      ? `https://${a.tenant_domain}/auctions/${a.id}/showroom`
                      : null;
                    const cardStyle = {
                      display: 'block',
                      background: '#fff',
                      border: '1px solid #E2E5EC',
                      borderLeft: '3px solid #1F50FF',
                      borderRadius: 6,
                      padding: '7px 9px',
                      textDecoration: 'none',
                      boxShadow: '0 1px 2px rgba(2,19,68,0.04)',
                      transition: 'opacity 0.15s',
                    };
                    const inner = (
                      <>
                        <div style={{ fontSize: '0.75rem', fontWeight: 500, color: '#021344', marginBottom: 3 }}>
                          {a.name || `Remate #${a.id}`}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: '#6B7A99', marginBottom: 4 }}>
                          {format(parseISO(a.start_date), 'HH:mm')}
                          {a.end_date && ` – ${format(parseISO(a.end_date), 'HH:mm')}`}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: '#9AA3B5', marginBottom: 4 }}>
                          {a.tenant_name}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          <StatusBadge status={a.status} />
                          {a.lot_count > 0 && (
                            <span style={{ fontSize: '0.65rem', color: '#9AA3B5' }}>{a.lot_count} lotes</span>
                          )}
                          <span style={{ fontSize: '0.65rem', color: '#9AA3B5' }}>
                            · {a.participant_count || 0} part.
                          </span>
                        </div>
                      </>
                    );

                    return href ? (
                      <a
                        key={a.id}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={cardStyle}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={a.id} style={cardStyle}>{inner}</div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

WeeklyCalendar.propTypes = {
  auctions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    status: PropTypes.string,
    tenant_name: PropTypes.string,
    tenant_domain: PropTypes.string,
    lot_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    participant_count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })).isRequired,
};

export default WeeklyCalendar;
